/**
 * TP/SL input block used by the main order form.
 *
 * Each of SL and TP can be entered as an ABSOLUTE PRICE or as a $-DISTANCE.
 * The block shows a live preview of the computed absolute price when in
 * distance mode so the operator sees exactly what will be sent to MetaAPI.
 */
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function TpSlBlock({ order, setOrder, livePrice, isAr }) {
  // Base price used for previewing distance → absolute:
  //   - Market: live bid/ask depending on side (ask for buy, bid for sell)
  //   - Pending: the entry_price the user typed
  const isPending = order.mode === 'pending';
  const isBuy = isPending
    ? order.orderType.startsWith('buy')
    : true; // in market mode we can't know side ahead; default to buy for preview
  const marketBase = livePrice
    ? (isBuy ? livePrice.ask : livePrice.bid)
    : null;
  const pendingBase = order.entryPrice ? Number(order.entryPrice) : null;
  const base = isPending ? pendingBase : marketBase;

  const previewAbs = (mode, val, kind /* 'sl' | 'tp' */) => {
    if (mode !== 'distance' || !val || !base || Number(val) <= 0) return null;
    const dist = Number(val);
    // TP: buy adds, sell subtracts.  SL: buy subtracts, sell adds.
    const addForTp = isBuy;
    const factor = kind === 'tp' ? (addForTp ? +1 : -1) : (addForTp ? -1 : +1);
    return Number((base + dist * factor).toFixed(5));
  };

  const slPreview = previewAbs(order.slMode, order.sl, 'sl');
  const tpPreview = previewAbs(order.tpMode, order.tp, 'tp');

  const renderInput = (kind /* 'sl' | 'tp' */) => {
    const mode = order[kind + 'Mode'];
    const setMode = (m) => setOrder({ ...order, [kind + 'Mode']: m });
    const label = kind === 'sl'
      ? (isAr ? 'ستوب لوز' : 'Stop Loss')
      : (isAr ? 'تيك بروفت' : 'Take Profit');
    const preview = kind === 'sl' ? slPreview : tpPreview;
    const accent = kind === 'sl' ? 'text-red-300' : 'text-emerald-300';

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-white/60 font-bold">{label}</Label>
          {/* Segmented Price / $ toggle — big, animated pill.
              We force LTR layout on the group so the sliding highlight
              always ends up under the correct button, regardless of the
              surrounding RTL/LTR direction of the page. */}
          <div
            dir="ltr"
            className="relative flex items-center p-1 rounded-lg bg-black/50 border border-white/15 shadow-inner"
            data-testid={`order-${kind}-mode-group`}
          >
            {/* Sliding highlight */}
            <div
              className="absolute top-1 bottom-1 rounded-md bg-[#d4af37] shadow-[0_0_10px_rgba(212,175,55,0.35)] transition-all duration-200 ease-out"
              style={{
                width: 'calc(50% - 4px)',
                left: mode === 'price' ? '4px' : 'calc(50% + 0px)',
              }}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => setMode('price')}
              className={`relative z-10 min-w-[46px] px-3 py-1 rounded-md text-[11px] font-black transition-colors duration-150 ${
                mode === 'price' ? 'text-black' : 'text-white/60 hover:text-white'
              }`}
              data-testid={`order-${kind}-mode-price`}
              title={isAr ? 'إدخال السعر المطلق مباشرة' : 'Enter absolute price'}
            >
              {isAr ? 'سعر' : 'Price'}
            </button>
            <button
              type="button"
              onClick={() => setMode('distance')}
              className={`relative z-10 min-w-[38px] px-3 py-1 rounded-md text-[13px] font-black leading-none transition-colors duration-150 ${
                mode === 'distance' ? 'text-black' : 'text-white/60 hover:text-white'
              }`}
              data-testid={`order-${kind}-mode-distance`}
              title={isAr ? 'المسافة بالدولار من سعر الدخول' : 'Distance in $'}
            >
              $
            </button>
          </div>
        </div>
        <Input
          type="number"
          value={order[kind]}
          onChange={(e) => setOrder({ ...order, [kind]: e.target.value })}
          placeholder={
            mode === 'distance'
              ? (isAr ? 'مثال: 3' : 'e.g. 3')
              : (isAr ? 'اختياري' : 'optional')
          }
          className="bg-black/40 border-white/10 text-white"
          data-testid={`order-${kind}`}
        />
        {mode === 'distance' && (
          <div className="text-[10px] text-white/50 leading-tight">
            {preview != null ? (
              <span>
                {isAr ? '← السعر النهائي:' : '→ Final price:'}{' '}
                <span className={`font-mono font-bold ${accent}`}>{preview}</span>
                {base != null && (
                  <span className="text-white/30">
                    {' '}({isPending ? (isAr ? 'من الدخول' : 'from entry') : (isAr ? 'من السوق' : 'from live')}{' '}
                    <span className="font-mono">{Number(base).toFixed(5)}</span>)
                  </span>
                )}
              </span>
            ) : (
              <span className="text-amber-300/70">
                {!base
                  ? (isPending
                      ? (isAr ? 'أدخل سعر الدخول أولاً' : 'Enter entry price first')
                      : (isAr ? 'بانتظار السعر الحي (يحتاج حساب متصل)' : 'Waiting for live price (need connected account)'))
                  : (isAr ? `أدخل المسافة بالدولار` : 'Enter distance in $')}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {livePrice && (order.slMode === 'distance' || order.tpMode === 'distance') && (
        <div className="flex items-center justify-between rounded-lg bg-blue-500/[0.06] border border-blue-500/30 px-2.5 py-1.5 text-[10px]">
          <span className="text-blue-200 font-bold">{isAr ? '● السعر الحي' : '● Live price'}</span>
          <div className="flex gap-3 font-mono">
            <span className="text-red-300">bid <span className="text-white">{Number(livePrice.bid).toFixed(5)}</span></span>
            <span className="text-emerald-300">ask <span className="text-white">{Number(livePrice.ask).toFixed(5)}</span></span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {renderInput('sl')}
        {renderInput('tp')}
      </div>
    </div>
  );
}
